/**
 * MatchHealthBar — fleet-level matching-quality monitoring strip shown above
 * the matches list. Surfaces summary coverage (the previously-invisible
 * generation-timeout backlog), total volume, flagged count, and the algorithmic
 * score distribution. Best-effort: hides on error/permission rather than block
 * the list.
 */
import { useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch } from '@/providers/admin-api';

interface MatchHealth {
  total: number;
  with_summary: number;
  missing_summary: number;
  coverage_pct: number;
  flagged: number;
  admin_created: number;
  by_status: { status: string; count: number }[];
  score_distribution: { bucket: string; count: number }[];
}

const SCORE_ORDER = ['75-100', '50-74', '25-49', '0-24', 'none'];
const SCORE_LABEL: Record<string, string> = {
  '75-100': '75–100',
  '50-74': '50–74',
  '25-49': '25–49',
  '0-24': '0–24',
  none: 'no score',
};

function Tile({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: 'default' | 'warn' | 'good';
}) {
  const valueColor =
    tone === 'warn' ? 'text-yellow-500' : tone === 'good' ? 'text-green-500' : 'text-foreground';
  return (
    <div className="rounded-md border bg-card px-4 py-3 min-w-[140px]">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 text-2xl font-bold ${valueColor}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function MatchHealthBar() {
  const apiUrl = useApiUrl();
  const [h, setH] = useState<MatchHealth | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminFetch(`${apiUrl}/v1/admin/matches/health`)
      .then((r) => r.json())
      .then((d: MatchHealth) => {
        if (!cancelled) setH(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  if (!h) return null;

  const coverageTone = h.coverage_pct >= 80 ? 'good' : h.coverage_pct >= 50 ? 'default' : 'warn';
  const buckets = [...h.score_distribution].sort(
    (a, b) => SCORE_ORDER.indexOf(a.bucket) - SCORE_ORDER.indexOf(b.bucket),
  );
  const maxBucket = Math.max(1, ...buckets.map((b) => b.count));

  return (
    <div className="mb-3 flex flex-wrap items-stretch gap-2">
      <Tile label="Total matches" value={h.total.toLocaleString()} />
      <Tile
        label="Summary coverage"
        value={`${h.coverage_pct}%`}
        sub={`${h.missing_summary.toLocaleString()} missing`}
        tone={coverageTone}
      />
      <Tile label="Flagged" value={h.flagged} tone={h.flagged > 0 ? 'warn' : 'default'} />
      <Tile label="Admin-created" value={h.admin_created} />

      {/* Score distribution mini-histogram */}
      <div className="flex-1 min-w-[260px] rounded-md border bg-card px-4 py-3">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          Score distribution
        </div>
        <div className="mt-2 flex items-end gap-2" style={{ height: 44 }}>
          {buckets.map((b) => (
            <div key={b.bucket} className="flex flex-1 flex-col items-center justify-end">
              <div
                className={`w-full rounded-sm ${b.bucket === 'none' ? 'bg-muted-foreground/40' : 'bg-primary/70'}`}
                style={{ height: `${Math.round((b.count / maxBucket) * 36) + 2}px` }}
                title={`${SCORE_LABEL[b.bucket] ?? b.bucket}: ${b.count}`}
              />
              <div className="mt-1 text-[10px] text-muted-foreground">{SCORE_LABEL[b.bucket] ?? b.bucket}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
